import {createContext, useCallback, useContext, useRef, useState} from 'react';
import {Profile} from '@stripe-internal/conversations-shared-dependencies';
import {ConfigContext} from '../ConfigProvider';
import {configureSentry} from '../../helpers/sentry';
import {HandleAuthFailureApiProvider} from './HandleAuthFailureApiProvider';
import {useApi} from '../ApiProvider/ApiProvider';
import {FileUploaderProvider} from '../FileUploaderProvider';

export const defaultProfile: Profile = {
  featureFlags: {
    enableUniversalHelp: false,
    universalHelpUseNewDesign: false,
    universalHelpUseDotComDesign: false,
    enableSupportConversationsEnforceAuth: false,
    universalHelpDotComFeedbackButtons: false,
    universalHelpDotComNoLlm: false,
    universalHelpDotComCsf: false,
    universalHelpUseInlineRacEmailForm: false,
    universalHelpEncourageMoreDetailFromUserMse5042: false,
    useExtractedConversationsAssistant: false,
    enableScreenshareUi: false,
    enableScreenshareContextProvider: false,
    enableRegionHeaderRouting: false,
    enableConversationsApiV2: false,
    enableConversationsThreads: false,
    enablePaidSupportAssistantCriticalIssueReporting: false,
    enableConversationsThreadsHistory: false,
    enableUhPendingInput: false,
    enableLiveChatSupportInMobile: false,
    enableExpressInactivityTimeout: false,
    enableExpressInactivityTimeoutSupportSite: false,
  },
  experiments: {
    supportFrontendTestExperiment: {
      variant: 'control',
      experimentName: 'support_frontend_text_experiment',
    },
  },
  livemode: false,
  visitor: {
    id: 'invalid-visitor',
    sessionType: 'anonymous',
    emailConfirmed: false,
  },
};

export const ProfileContext = createContext<{
  profile: Profile;
  setProfile: (profile: Profile) => void;
  reloadProfile: () => void;
}>({
  profile: defaultProfile,
  setProfile: () => {},
  reloadProfile: async () => null,
});

export function useProfile() {
  return useContext(ProfileContext);
}

type Props = {
  children: JSX.Element;
};

export function ProfileProvider({children}: Props) {
  const config = useContext(ConfigContext);
  const {
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    revision,
    publicSentryDsn,
    livemode,
    supportPlan,
  } = config;

  const {fetchProfile} = useApi();

  const initialProfile: Profile = {
    ...defaultProfile,
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    livemode,
    supportPlan,
  };
  const [profile, setProfile] = useState<Profile>(initialProfile);

  const fetching = useRef<boolean>(false);

  const configWithProfile = {
    ...config,
    visitor: profile.visitor,
    user: profile.user,
    merchant: profile.merchant,
    featureFlags: profile.featureFlags,
    experiments: profile.experiments,
  };

  const onReloadProfile = useCallback(async () => {
    if (fetching.current) {
      return null;
    }

    try {
      fetching.current = true;

      const response = await fetchProfile({
        audience: config.audience.toString(),
        entryPoint: config.entrypoint?.toString() ?? 'dashboard',
        livemode: config.livemode,
        attemptNumber: 0,
        requiresSession: false,
      });

      fetching.current = false;

      if (response.type === 'success') {
        setProfile(response.profile);
      }
    } catch (e) {
      // intentionally empty
    }

    return null;
  }, [config, fetchProfile]);

  const onSetProfile = useCallback(
    (newProfile: Profile) => {
      setProfile(newProfile);

      if (publicSentryDsn) {
        configureSentry(publicSentryDsn, revision, newProfile.visitor);
      }
    },
    [publicSentryDsn, revision],
  );

  return (
    <ProfileContext.Provider
      value={{
        profile,
        setProfile: onSetProfile,
        reloadProfile: onReloadProfile,
      }}
    >
      {/* re-provide config in case child components pull profile values from the config context */}
      <ConfigContext.Provider value={configWithProfile}>
        <HandleAuthFailureApiProvider>
          <FileUploaderProvider>{children}</FileUploaderProvider>
        </HandleAuthFailureApiProvider>
      </ConfigContext.Provider>
    </ProfileContext.Provider>
  );
}
